import { useMemo, useState } from 'react';
import { API_BASE } from '../components/Config.js';
import { scrollToAnchor } from '../utilities/scrollUtil.js';

// Animations
import CyclingIcon1 from '../assets/animations/cyclist_json1.json';
import CyclingIcon2 from '../assets/animations/cyclist_json2.json';
import CyclingIcon3 from '../assets/animations/cyclist_json3.json';
import CyclingIcon4 from '../assets/animations/cyclist_json4.json';
import CyclingIcon5 from '../assets/animations/cyclist_json5.json';
import CyclingIcon6 from '../assets/animations/cyclist_json2.json';
import CyclingIcon7 from '../assets/animations/cyclist_json5.json';
import CyclingIcon8 from '../assets/animations/cyclist_json3.json';

export default function useUploadSubmit({
  jobTitle,
  jobDescription,
  keywordsLocked,
  editableKeywords,
  bulkFiles,
  pendingFilenameInputs,
  submittedFilenameInputs,
  setSubmittedFilenameInputs,
  setEditedFiles,
  setResponseJson,
  setRankedResumesWithOriginalIndex,
  setUploadError,
  isLoading,
  setIsLoading,
  triggerConfetti,
  clearPreviewCache,
}) {
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentCyclingIcon, setCurrentCyclingIcon] = useState(null);

  const cyclingIcons = useMemo(
    () => [
      CyclingIcon1,
      CyclingIcon2,
      CyclingIcon3,
      CyclingIcon4,
      CyclingIcon5,
      CyclingIcon6,
      CyclingIcon7,
      CyclingIcon8,
    ],
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCurrentCyclingIcon(cyclingIcons[Math.floor(Math.random() * cyclingIcons.length)]);
    if (keywordsLocked && editableKeywords.length === 0) {
      setUploadError('No keywords to compare against - unlock and extract or add some first');
      return;
    }
    setUploadError('');
    setIsLoading(true);
    setProcessingProgress(0);

    try {
      const formData = new FormData();
      formData.append('job_title', jobTitle.trim());
      if (keywordsLocked) {
        formData.append('use_locked_keywords', 'true');
        formData.append('current_keywords', JSON.stringify(editableKeywords));
      } else {
        formData.append('use_locked_keywords', 'false');
        formData.append('job_description', jobDescription.trim());
      }

      setProcessingProgress(0);
      if (!jobTitle.trim()) throw new Error('Job title is required');
      scrollToAnchor('lottie-container-large', -10);
      if (!jobDescription.trim()) throw new Error('Job description is required');

      formData.append('job_title', jobTitle.trim());
      formData.append('job_description', keywordsLocked ? '' : jobDescription.trim());
      formData.append('use_locked_keywords', keywordsLocked);
      if (bulkFiles.length === 0) throw new Error('No valid file added');
      bulkFiles.forEach((file) => {
        if (!file) throw new Error('Invalid file detected');
        formData.append('resumes', file, file.name);
      });

      const reindexedPendingInputs = {};
      const reindexedEditedFlags = {};
      bulkFiles.forEach((file, newIdx) => {
        const originalIdx = file.originalIndex ?? newIdx;
        const label = pendingFilenameInputs[originalIdx] ?? submittedFilenameInputs[originalIdx];
        if (label !== undefined) {
          reindexedPendingInputs[newIdx] = label;
          reindexedEditedFlags[newIdx] = true;
        }
      });
      const mergedEdits = { ...submittedFilenameInputs, ...reindexedPendingInputs };
      setSubmittedFilenameInputs(reindexedPendingInputs);
      setEditedFiles(reindexedEditedFlags);
      formData.append('edited_filename_inputs', JSON.stringify(mergedEdits));

      const progressInterval = setInterval(() => {
        setProcessingProgress((prev) => Math.min(prev + 4, 90));
      }, 100);
      const interval = setInterval(() => {
        setProcessingProgress((prev) => {
          const next = prev + 5;
          if (next >= 100) {
            clearInterval(interval);
            return 100;
          }
          return next;
        });
      }, 100);

      const res = await fetch(`${API_BASE}/upload`, { method: 'POST', body: formData });
      clearInterval(progressInterval);
      setProcessingProgress(100);
      const data = await res.json();
      if (!res.ok) {
        const errorMsg = data?.error || data?.message || 'Upload failed';
        setProcessingProgress(0);
        throw new Error(errorMsg);
      }

      setResponseJson({
        job_title: data.job_title || jobTitle,
        job_description: data.job_description || jobDescription,
        job_description_keywords: Array.isArray(data.job_description_keywords) ? data.job_description_keywords : [],
        ranked_resumes: (data.ranked_resumes || []).map((resume) => ({
          filename: resume.filename || 'Unknown',
          label: resume.label || 'Untitled',
          similarity_score: Number(resume.similarity_score) || 0,
          matching_keywords: Array.isArray(resume.matching_keywords) ? resume.matching_keywords : [],
          content: resume.content || '',
          file_content: resume.file_content || '',
          preprocessed: resume.preprocessed || [],
          preview_image: resume.preview_image || null,
        })),
        uploaded_files: data.uploaded_files || [],
      });

      const { ranked_resumes, uploaded_files } = data;
      const rankedResumesWithOriginalIndexLocal = (ranked_resumes || []).map((resume) => {
        const origIdx = uploaded_files.findIndex((name) => name === resume.filename);
        return { ...resume, originalIndex: origIdx };
      });
      setRankedResumesWithOriginalIndex(rankedResumesWithOriginalIndexLocal);

      clearPreviewCache?.();
      if (processingProgress < 100) setProcessingProgress(100);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'upload_form_success',
        jobTitle: data.job_title,
        fileCount: data.uploaded_files.length,
        keywordsCount: data.job_description_keywords.length,
        timestamp: new Date().toISOString(),
      });
      triggerConfetti?.();
      setTimeout(() => {
        scrollToAnchor('keyword-count-number', 200);
      }, 300);
    } catch (err) {
      setUploadError(
        err.message.includes('Server error')
          ? "The server couldn't process your request. Please check your inputs and try again."
          : err.message
      );
    } finally {
      setIsLoading(false);
      setCurrentCyclingIcon(null);
      setTimeout(() => {
        setProcessingProgress(0);
      }, 1000);
    }
  };

  return { handleSubmit, processingProgress, currentCyclingIcon };
}


